import { followUpItemSchema } from "./medical-evidence";
import { z } from "zod";
import { patientEmailStatusSchema } from "./patient";
import { productFeedbackConsentStateSchema } from "./product-feedback-consent";
import { formSubmissionSchema } from "./forms";
import { goalProgressSchema } from "./health";
import { goalZoneDoctorSummarySchema } from "./goal-zone";
import { meetingDocumentationRecordSchema } from "./meeting";
import { labReferenceBucketSchema, labReviewOutcomeSchema } from "./labs";
import {
  labOrderLabAttachmentSchema,
  labOrderLabCommentSchema,
  labOrderQuarantinedReportSchema,
} from "./labportalen";
import { patientGroupSchema } from "./patient-group";
import { onboardingStatusSchema } from "./onboarding-status";
import { onboardingTimelineStepSchema } from "./onboarding-timeline";
import { treatmentStartStateSchema } from "./treatment-start";
import { treatmentEndReasonSchema } from "./treatment-status-decision";
import {
  bloodPressurePulseReadingSchema,
  clinicalStageSchema,
  clinicianSubscriptionSchema,
} from "./patient-header";
import { preConsultSummarySchema } from "./preconsult-summary";
import { f01SafetyAnswerSchema } from "./f01-safety-answers";
import {
  meetingOrderIsEmpty,
  parsePrescriptionNote,
  prefillDose,
  type PrescriptionOrder,
} from "./prescription-cycle";
import {
  prescriptionTransferReviewStatusSchema,
  transferReviewSchema,
} from "./prescription-transfer-review";

// CAR-12 — clinician inbox + patient detail. The inbox is the
// triage view: one row per patient with their newest intake outcome.
// PatientDetail expands a single patient row into their treatment,
// cycles, and submission history.

export const inboxRowSchema = z.object({
  patientId: z.string().uuid(),
  // Patient's name from BankID, "Given Surname" — null for pre-name-capture
  // rows, dev-auth patients, and anonymised rows. Clinicians identify by name;
  // the personnummer shorthand stays for disambiguation.
  name: z.string().nullable(),
  personalNumberLast6: z.string(),
  submittedAt: z.string().nullable(),
  formSubmissionId: z.string().uuid().nullable(),
  templateVersion: z.number().int().nullable(),
  outcome: z.enum(["clean", "screening_failed"]),
  flagCount: z.number().int(),
  treatmentState: z.string(),
  // Which of the two journeys she is on. The inbox is where triage happens, so
  // a prescription-transfer ("receptövertag") patient must be distinguishable
  // here and not only once someone has opened her — she must never be sent to
  // a consultation.
  patientGroup: patientGroupSchema,
  // TES-458 — where her prescription transfer stands, so an undecided review
  // is tellable from an approved one (and from an F-07 not yet in: null)
  // without opening each row. Always null for a NEW_TREATMENT patient.
  transferReviewStatus: prescriptionTransferReviewStatusSchema.nullable(),
});
export type InboxRow = z.infer<typeof inboxRowSchema>;

// CAR-13 — patient list / search. Broader than the inbox: every patient in
// the doctor's clinic (not just those awaiting clinical action), searchable
// by the last digits of their personal number. One lightweight row per
// patient; the detail view expands it.
export const patientListRowSchema = z.object({
  patientId: z.string().uuid(),
  name: z.string().nullable(),
  personalNumberLast6: z.string(),
  // TES-474 #5 — the same journey ("Resa") the inbox shows: "receptövertag" /
  // "ny behandling".
  patientGroup: patientGroupSchema,
  treatmentState: z.string(),
  createdAt: z.string(),
  lastSubmissionAt: z.string().nullable(),
  hasIntake: z.boolean(),
  // TES-1117 — the precise status next to the journey; "Onboarding" (the
  // treatment state above) is only the grouping. Optional so an admin built
  // before this reads a newer server, and the reverse.
  onboardingStatus: onboardingStatusSchema.optional(),
});
export type PatientListRow = z.infer<typeof patientListRowSchema>;

// TES-1308 — the list is paged over the whole clinic, and the search and the
// journey filter run server-side before the page is cut, so a patient older
// than any cap is found. `limit` defaults to PATIENT_LIST_PAGE_SIZE.
export const PATIENT_LIST_PAGE_SIZE = 100;
export const PATIENT_LIST_MAX_LIMIT = 200;
export const patientListQuerySchema = z.object({
  q: z.string().max(100).optional(),
  group: patientGroupSchema.optional(),
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(PATIENT_LIST_MAX_LIMIT)
    .default(PATIENT_LIST_PAGE_SIZE),
});
export type PatientListQuery = z.infer<typeof patientListQuerySchema>;

// What every page carries besides its rows: where it sits, and the counts for
// the whole match so the filter chips and "x av y" do not depend on the page.
export const patientListPageSchema = z.object({
  // Patients matching the search AND the journey filter, across all pages.
  total: z.number().int(),
  offset: z.number().int(),
  limit: z.number().int(),
  // Patients matching the search per journey, before the journey filter.
  groupCounts: z.object({
    NEW_TREATMENT: z.number().int(),
    PRESCRIPTION_TRANSFER: z.number().int(),
  }),
});

export const patientListSchema = patientListPageSchema.extend({
  rows: z.array(patientListRowSchema),
});
export type PatientList = z.infer<typeof patientListSchema>;

// TES-756 — the schedule is dated, not recurring. A block belongs to a
// concrete date; two weeks never need to look alike.
//
// `date` is an ISO date (YYYY-MM-DD) in the clinician's zone — not a
// timestamp. A date without a time cannot slip to the wrong day when it
// crosses a time-zone boundary, which a Date in JSON does as soon as anyone
// serializes it.
export const scheduleDayBlockSchema = z
  .object({
    startMinute: z.number().int().min(0).max(1439),
    endMinute: z.number().int().min(1).max(1440),
  })
  .refine((b) => b.endMinute > b.startMinute, {
    message: "endMinute must be after startMinute",
    path: ["endMinute"],
  });
export type ScheduleDayBlock = z.infer<typeof scheduleDayBlockSchema>;

export const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// TES-1238 — the first pair of blocks on one day that overlap, earliest first,
// or null. Touching is fine (09:00–12:00 and 12:00–15:00). Shared by the API's
// refusal and admin's inline error so the two never disagree.
export function findScheduleOverlap(
  blocks: readonly ScheduleDayBlock[],
): [ScheduleDayBlock, ScheduleDayBlock] | null {
  const sorted = [...blocks].sort(
    (a, b) => a.startMinute - b.startMinute || a.endMinute - b.endMinute,
  );
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i]!.startMinute < sorted[i - 1]!.endMinute) {
      return [sorted[i - 1]!, sorted[i]!];
    }
  }
  return null;
}

// "09:00" from minutes into the day. 1440 reads "24:00".
export function scheduleMinuteLabel(minute: number): string {
  const h = String(Math.floor(minute / 60)).padStart(2, "0");
  const m = String(minute % 60).padStart(2, "0");
  return `${h}:${m}`;
}

export const scheduleDaySchema = z.object({
  date: z.string().regex(ISO_DATE_RE, "date must be YYYY-MM-DD"),
  blocks: z.array(scheduleDayBlockSchema).max(6),
});
export type ScheduleDay = z.infer<typeof scheduleDaySchema>;

// One week's schedule, as admin reads and writes it. `weekStart` is always a
// Monday; the API rejects other days rather than silently normalizing, so
// two clients can never write the "same" week under different keys.
export const weekScheduleSchema = z.object({
  weekStart: z.string().regex(ISO_DATE_RE, "weekStart must be YYYY-MM-DD"),
  tz: z.string().min(1),
  slotMinutes: z.number().int().min(5).max(120),
  days: z.array(scheduleDaySchema).max(7),
  // TES-1238 — the clinician's per-hour meeting cap, shown where the schedule
  // is edited. Null = no cap. Optional so a reader built before it existed
  // still parses the response.
  maxMeetingsPerHour: z.number().int().min(1).nullable().optional(),
  // TES-1261 — minutes from one bookable start to the next. Null = the same
  // as slotMinutes. Optional for the same reason as the cap.
  slotIntervalMinutes: z.number().int().min(5).max(120).nullable().optional(),
});
export type WeekSchedule = z.infer<typeof weekScheduleSchema>;

// TES-1238 — at most this many meetings may start in one clock hour (in the
// clinician's scheduleTz). Null = no cap. The upper bound only guards against
// a typo: a 20-minute slot grid offers at most 3 an hour anyway.
export const MAX_MEETINGS_PER_HOUR_LIMIT = 12;
// TES-1261 — the time from one bookable start to the next (Kirill: a
// 20-minute meeting every 30 minutes). Null = the same as the meeting length.
// It may not be shorter than the meeting; that needs the clinician's
// slotMinutes, so it is checked with slotIntervalError below.
export const MAX_SLOT_INTERVAL_MINUTES = 120;
const maxMeetingsPerHourSchema = z
  .number()
  .int()
  .min(1)
  .max(MAX_MEETINGS_PER_HOUR_LIMIT)
  .nullable();
const slotIntervalMinutesSchema = z
  .number()
  .int()
  .min(5)
  .max(MAX_SLOT_INTERVAL_MINUTES)
  .nullable();
// TES-1409 — the meeting length (employees.slotMinutes), set by admin where
// the interval is. 10–60 in steps of 5: the booking grid (expandBlock) takes
// any length, but every start stays on a five-minute clock time and one
// meeting never outgrows a working window. Only new bookings use it; a booked
// meeting keeps its stored durationMinutes.
export const MIN_SLOT_MINUTES = 10;
export const MAX_SLOT_MINUTES = 60;
export const SLOT_MINUTES_STEP = 5;
const slotMinutesSchema = z
  .number()
  .int()
  .min(MIN_SLOT_MINUTES)
  .max(MAX_SLOT_MINUTES)
  .multipleOf(SLOT_MINUTES_STEP);
// `slotIntervalMinutes` and `slotMinutes` left out = unchanged, so a client
// that only knows the cap still sends a valid update.
export const scheduleSettingsUpdateSchema = z.object({
  maxMeetingsPerHour: maxMeetingsPerHourSchema,
  slotIntervalMinutes: slotIntervalMinutesSchema.optional(),
  slotMinutes: slotMinutesSchema.optional(),
});
export type ScheduleSettingsUpdate = z.infer<
  typeof scheduleSettingsUpdateSchema
>;
// The response carries the stored length, which may predate the 10–60 rule,
// so it is read with the week's looser bounds.
export const scheduleSettingsSchema = z.object({
  maxMeetingsPerHour: maxMeetingsPerHourSchema,
  slotIntervalMinutes: slotIntervalMinutesSchema,
  slotMinutes: z.number().int().min(5).max(120),
});
export type ScheduleSettings = z.infer<typeof scheduleSettingsSchema>;

// TES-1409 — why a meeting length cannot be saved, or null when it can.
// Shared by admin's inline error; the API refuses the same through the schema.
export function slotMinutesError(
  slotMinutes: number,
): "out_of_range" | "not_step" | null {
  if (slotMinutes < MIN_SLOT_MINUTES || slotMinutes > MAX_SLOT_MINUTES) {
    return "out_of_range";
  }
  if (slotMinutes % SLOT_MINUTES_STEP !== 0) return "not_step";
  return null;
}

// TES-1261 — why an interval cannot be saved, or null when it can. Shared by
// the API's refusal and admin's inline error. Null (unset) is always valid.
export function slotIntervalError(
  intervalMinutes: number | null,
  slotMinutes: number,
): "too_short" | "too_long" | null {
  if (intervalMinutes === null) return null;
  if (intervalMinutes < slotMinutes) return "too_short";
  if (intervalMinutes > MAX_SLOT_INTERVAL_MINUTES) return "too_long";
  return null;
}

// TES-1238 — a weekly pattern applied over a date range: "Tuesdays 09–12 and
// 13–16, Thursdays 09–12 and 15–18, from 13 Oct to 26 Nov". Zeinab entered
// Kirill's hours week by week; this writes them in one go.
//
// `weekday` is ISO (1 = Monday … 7 = Sunday). A weekday not in `weekdays` is
// left alone on every date. A listed weekday with no blocks clears that day.
//
// A date that already has blocks DIFFERENT from the pattern is a conflict:
// with `overwrite: false` it is skipped and returned in `conflicts`, so the
// caller can ask before replacing someone's day. With `overwrite: true` it is
// replaced. A date that already matches is left as is.
export const schedulePatternWeekdaySchema = z.object({
  weekday: z.number().int().min(1).max(7),
  blocks: z.array(scheduleDayBlockSchema).max(6),
});
export type SchedulePatternWeekday = z.infer<
  typeof schedulePatternWeekdaySchema
>;

export const schedulePatternApplySchema = z
  .object({
    from: z.string().regex(ISO_DATE_RE, "from must be YYYY-MM-DD"),
    to: z.string().regex(ISO_DATE_RE, "to must be YYYY-MM-DD"),
    weekdays: z.array(schedulePatternWeekdaySchema).min(1).max(7),
    overwrite: z.boolean().default(false),
  })
  .refine((p) => p.to >= p.from, {
    message: "to must not be before from",
    path: ["to"],
  })
  // No further than admin may plan ahead at all (SCHEDULE_EDITABLE_WEEKS).
  .refine(
    (p) =>
      (Date.parse(`${p.to}T00:00:00Z`) - Date.parse(`${p.from}T00:00:00Z`)) /
        86_400_000 <
      SCHEDULE_EDITABLE_WEEKS * 7,
    { message: "the range is too long", path: ["to"] },
  )
  .refine(
    (p) => new Set(p.weekdays.map((w) => w.weekday)).size === p.weekdays.length,
    { message: "each weekday at most once", path: ["weekdays"] },
  );
export type SchedulePatternApply = z.input<typeof schedulePatternApplySchema>;

export const schedulePatternResultSchema = z.object({
  // Dates written (empty before, or replaced with `overwrite`).
  applied: z.array(z.string().regex(ISO_DATE_RE)),
  // Dates that already matched the pattern.
  unchanged: z.array(z.string().regex(ISO_DATE_RE)),
  // Dates with other blocks, left alone because `overwrite` was false.
  conflicts: z.array(z.string().regex(ISO_DATE_RE)),
});
export type SchedulePatternResult = z.infer<typeof schedulePatternResultSchema>;

// The write takes only the days: tz and slotMinutes belong to the clinician,
// not to the week, and are changed in a place of their own.
export const weekScheduleUpdateSchema = z.object({
  weekStart: z.string().regex(ISO_DATE_RE, "weekStart must be YYYY-MM-DD"),
  days: z.array(scheduleDaySchema).max(7),
});
export type WeekScheduleUpdate = z.infer<typeof weekScheduleUpdateSchema>;

// How many weeks ahead admin may edit (Simon 2026-09-17).
// The booking horizon is 14 days; 8 weeks gives room to plan ahead.
export const SCHEDULE_EDITABLE_WEEKS = 8;

export const patientDetailCycleSchema = z.object({
  id: z.string().uuid(),
  cycleNumber: z.number().int(),
  state: z.string(),
  dose: z.string(),
  stateChangedAt: z.string(),
  expiresAt: z.string().nullable(),
  // TES-997 — whether a named doctor recorded an issued prescription on this
  // cycle ("Nytt recept är utfärdat i Webdoc"), and the choice made at the
  // publish that decided it. Optional so an older server still parses.
  prescribedAt: z.string().nullable().optional(),
  prescriptionChoice: z.string().nullable().optional(),
  existingPrescriptionValidUntil: z.string().nullable().optional(),
  // TES-1291 — the order's medication on its own, so the decision workspace
  // can tell a medication switch from a dose change. Optional so an older
  // server still parses.
  medication: z.string().nullable().optional(),
});
export type PatientDetailCycle = z.infer<typeof patientDetailCycleSchema>;

// CAR-117 — doctor lab-approval gate. The clinician sees each lab order with
// its resulted analytes (value + reference bucket so out-of-range markers
// render) and reviews the order before the patient's treatment can activate.
//
// TES-890 — `reviewedAt` set no longer means approved; it means a doctor
// looked. `reviewOutcome === "approved"` is the gate. Reading the timestamp
// alone would treat "nytt prov krävs" as a sign-off.
// TES-1341 — one earlier value of a lab result that a later LabPortalen
// delivery replaced. `replacedBy` is "correction" when the lab corrected it
// (StatusCode C or IsReplacingFinal), "update" for any other change (e.g. a
// preliminary value turned final).
export const labResultHistoryEntrySchema = z.object({
  value: z.number().nullable(),
  valueText: z.string().nullable(),
  unit: z.string(),
  observedAt: z.string().nullable(),
  replacedAt: z.string(),
  replacedBy: z.enum(["correction", "update"]),
  preliminary: z.boolean(),
});
export type LabResultHistoryEntry = z.infer<typeof labResultHistoryEntrySchema>;

export const clinicianLabResultSchema = z.object({
  analyteKey: z.string(),
  // i18n key, e.g. 'labs.analytes.hba1c'
  displayKey: z.string(),
  unit: z.string(),
  value: z.number().nullable(),
  valueText: z.string().nullable(),
  observedAt: z.string(),
  bucket: labReferenceBucketSchema,
  refLow: z.number().nullable(),
  refHigh: z.number().nullable(),
  // TES-1337 — the LAB's free-text comment on this value (LabPortalen),
  // verbatim. Not the doctor's comment to the patient. Optional so an older
  // API response still parses.
  labComment: z.string().nullable().optional(),
  // TES-1337 (InfoSolutions 10.3) — the value came in a reply the lab called
  // "Preliminärsvar" (StatusCode P/PPA) and may still change; the lab's own
  // reference interval as it displays it, and its out-of-range mark ("*").
  // Optional so an older API response still parses.
  labPreliminary: z.boolean().optional(),
  labReference: z.string().nullable().optional(),
  labRefMark: z.string().nullable().optional(),
  // TES-1341 — when a LabPortalen correction (StatusCode C, or a replacing
  // final analysis) last replaced this value, and the values it replaced,
  // oldest first. Optional so an older API response still parses.
  labCorrectedAt: z.string().nullable().optional(),
  labValueHistory: z.array(labResultHistoryEntrySchema).optional(),
});
export type ClinicianLabResult = z.infer<typeof clinicianLabResultSchema>;

export const clinicianLabOrderSchema = z.object({
  id: z.string().uuid(),
  // Webdoc lifecycle: ordered | remiss_sent | partial | resulted | cancelled.
  status: z.string(),
  orderedAt: z.string(),
  // Non-null once a doctor has REVIEWED a resulted order — whatever he
  // concluded. It is `reviewOutcome` that says whether the gate opens.
  reviewedAt: z.string().nullable(),
  reviewedByDoctorId: z.string().nullable(),
  // TES-890 (Ö-185): approved results ("godkänt underlag"), supplement needed
  // ("komplettering krävs"), or a new test needed ("nytt prov krävs").
  // Optional so an older API response still parses; null on an order nobody
  // has reviewed.
  reviewOutcome: labReviewOutcomeSchema.nullable().optional(),
  // TES-506 — when a human marked that the results had come in, outside the
  // Webdoc integration. Optional so an older API response still parses.
  resultsReceivedAt: z.string().nullable().optional(),
  // TES-442 — the remiss number the doctor typed, and the requisition ids
  // learned from the first LabPortalen report attached. Optional so an older
  // API response still parses.
  //
  // TES-1341 — in InfoSolutions' terms, `labPortalenRid` is the LID
  // (Requisition@RequisitionID, the performing lab's number), NOT the RID;
  // the name is historical. `labPortalenExternalRid` is the RID
  // (Requisition@ExternalRequisitionID, set by the ordering system, normally
  // LabPortalen), null when the report carried none.
  requisitionRef: z.string().nullable().optional(),
  labPortalenRid: z.string().nullable().optional(),
  labPortalenExternalRid: z.string().nullable().optional(),
  // TES-1341 — files the lab attached (name and type only; the file stays in
  // LabPortalen). Optional so an older API response still parses.
  labAttachments: z.array(labOrderLabAttachmentSchema).optional(),
  // TES-1179 — open quarantined LabPortalen reports whose requisition names
  // this order, newest first. A read-side annotation: the order's status is
  // untouched. Optional so an older API response still parses.
  quarantinedReports: z.array(labOrderQuarantinedReportSchema).optional(),
  // TES-1337 — the lab's comments on the whole order, one per delivery,
  // oldest first. Optional so an older API response still parses.
  labComments: z.array(labOrderLabCommentSchema).optional(),
  // TES-1337 — panel analyte keys with no value yet, on an order that has
  // some (one order's values may arrive in several deliveries). Empty when
  // nothing has arrived or everything has. Optional for older responses.
  missingAnalytes: z.array(z.string()).optional(),
  // TES-1370 (Ö-582) — the doctor's panel to review, approve and publish the
  // patient texts applies to this order: the box has publishing on and the
  // order has values (partial or resulted). Optional for older responses
  // (absent = not available).
  patientFeedbackAvailable: z.boolean().optional(),
  results: z.array(clinicianLabResultSchema),
});
export type ClinicianLabOrder = z.infer<typeof clinicianLabOrderSchema>;

// TES-201 — doctor-curated, patient-facing lab feedback. The doctor reviews a
// resulted order, edits a per-analyte comment + an overall summary, then
// "Godkänn och skicka till patient" — a strict superset of the CAR-117 approve
// gate (it sets feedbackSentAt and, if still unreviewed, reviewedAt too).

export const clinicianLabFeedbackCommentSchema = z.object({
  analyteKey: z.string(),
  comment: z.string().max(500),
});
export type ClinicianLabFeedbackComment = z.infer<
  typeof clinicianLabFeedbackCommentSchema
>;

// Save/send request body — the doctor's edits to the (draft-prefilled) panel.
export const clinicianLabFeedbackInputSchema = z.object({
  // Overall "Läkarens kommentar (syns för patienten)" — the ~1000-char field.
  patientSummary: z.string().max(1000),
  comments: z.array(clinicianLabFeedbackCommentSchema),
});
export type ClinicianLabFeedbackInput = z.infer<
  typeof clinicianLabFeedbackInputSchema
>;

// GET / save / send response. TES-283 (spec §7): the two layers travel
// SEPARATELY — `comment`/`patientSummary` are the clinician's editable
// assessment (empty until written; the AI draft no longer pre-fills them),
// and the aiDraft* fields carry the stored generated layer (immutable,
// materialized once when the order is resulted; null before that).
export const clinicianLabFeedbackSchema = z.object({
  orderId: z.string().uuid(),
  patientSummary: z.string(),
  // Non-null once feedback has been sent to the patient (the visibility gate).
  feedbackSentAt: z.string().nullable(),
  // Non-null once the order is approved (the CAR-117 activation gate). Sending
  // feedback sets this too if it was still null.
  reviewedAt: z.string().nullable(),
  // TES-283 — layer 2, read-only: what the AI suggested.
  aiDraftSummary: z.string().nullable(),
  aiDraftGeneratorVersion: z.string().nullable(),
  aiDraftGeneratedAt: z.string().nullable(),
  // Analyte keys outside the reference range at generation time (the spec's
  // layer-2 "riskflaggor").
  aiDraftRiskFlags: z.array(z.string()),
  // TES-1370 (Ö-582) — explicit approval per item and what is published.
  // All optional so an older API response still parses.
  //
  // The order's status: 'partial' may be published as a "Delsvar" too.
  orderStatus: z.string().optional(),
  // Panel analyte keys of this order with no value from the lab yet.
  missingAnalyteKeys: z.array(z.string()).optional(),
  // Set when the doctor approved `patientSummary` as it stands; cleared by
  // an edit of it and by any new or changed value from the lab.
  summaryApprovedAt: z.string().nullable().optional(),
  // The last publish under the approval model, and the summary the patient
  // reads (a snapshot, so later draft edits never reach her by themselves).
  publishedAt: z.string().nullable().optional(),
  publishedSummary: z.string().nullable().optional(),
  // The draft wording's review state ('qa_working_assumption_pending_kirill'
  // for the TES-1370 template), shown to the doctor beside the draft.
  draftReviewStatus: z.string().nullable().optional(),
  results: z.array(
    clinicianLabResultSchema.extend({
      comment: z.string(),
      aiDraftComment: z.string().nullable(),
      // TES-1370 — approval of `comment` as it stands, and the published
      // state of this value and its explanation.
      commentApprovedAt: z.string().nullable().optional(),
      publishedAt: z.string().nullable().optional(),
      publishedComment: z.string().nullable().optional(),
      // TES-1370 — the lab's own words and marks, for the doctor only. Input
      // to his explanation; never published to the patient verbatim.
      labComment: z.string().nullable().optional(),
      labPreliminary: z.boolean().optional(),
    }),
  ),
});
export type ClinicianLabFeedback = z.infer<typeof clinicianLabFeedbackSchema>;

// TES-1370 (Ö-582) — the doctor approves (or withdraws his approval of) one
// patient text: the summary of the whole sample, or the explanation of one
// value. `text` is the exact text he approves; it is saved with the approval
// so the two can never differ. Withdrawing needs no text.
export const clinicianLabFeedbackApprovalInputSchema = z.discriminatedUnion(
  "approved",
  [
    z.object({
      approved: z.literal(true),
      target: z.enum(["summary", "value"]),
      analyteKey: z.string().min(1).optional(),
      text: z.string().max(1000),
    }),
    z.object({
      approved: z.literal(false),
      target: z.enum(["summary", "value"]),
      analyteKey: z.string().min(1).optional(),
    }),
  ],
);
export type ClinicianLabFeedbackApprovalInput = z.infer<
  typeof clinicianLabFeedbackApprovalInputSchema
>;

// TES-506 — a care meeting as it appears on the patient page: when, why,
// where it landed, and what the doctor wrote. Its own schema node (not
// clinicianMeetingSchema) because the latter carries the patient label —
// which is exactly what you do NOT need on the patient's own page.
export const clinicianPatientMeetingSchema = z.object({
  id: z.string().uuid(),
  scheduledAt: z.string(),
  durationMinutes: z.number().int(),
  reason: z.string(),
  status: z.string(),
  workflowStatus: z.string(),
  doctorDisplayName: z.string().nullable(),
  // TES-811 (Ö-184) — may the signed-in doctor open this meeting's detail?
  //
  // The patient page used to link EVERY meeting to a detail view, regardless
  // of who it was booked on, so Emelie clicked through to a guaranteed 403
  // that read "Du saknar behörighet för den här sidan" — and reported a
  // permission bug for what was really someone else's consult.
  //
  // Computed server-side because the rule lives there: the booked doctor, or
  // the patient's current responsible doctor (TES-804's handover log). A
  // client-side comparison would be a second copy of an access rule, and the
  // two would drift.
  //
  // Optional so an older API response still parses; the client treats a
  // missing value as "openable", which is the behaviour it had before.
  canOpen: z.boolean().optional(),
  documentation: meetingDocumentationRecordSchema.nullable(),
  // TES-434 B — the patient's own 1–5 answer to "Hur var ditt vårdmöte?".
  patientRating: z.number().int().min(1).max(5).nullable(),
  patientRatedAt: z.string().nullable(),
});
export type ClinicianPatientMeeting = z.infer<
  typeof clinicianPatientMeetingSchema
>;

// TES-691 — WHICH meeting note the treatment decision should show.
//
// The note is written at the meeting; the prescription is set later, under
// "Godkänn behandling". Three of the four MEETING_NEXT_STEPS give
// treatmentProceed: false, so lab results usually come between the two. The
// doctor should not have to re-type herself from memory — but the decision
// stays explicit, so this only ever DISPLAYS the note. It is never parsed and
// never pre-fills the fields.
//
// (TES-956 superseded the "never parsed" half: the decision now prefills from
// the meeting's STRUCTURED order, and parses this note only as a fallback for
// a meeting documented before that — see `meetingOrderPrefill`.)
//
// The thing the order's fields cannot hold is a TITRATION PLAN across cycles:
// "0,25 mg i 4 veckor, sedan 0,5 mg". Titration is modelled as a separate
// adjustDose (reason "titration"), not as a schedule inside one ordination
// string, so a parser fed that note must either drop half of it or mangle the
// rest — and it would be right often enough that nobody would check. Ö-26
// settled the same principle for the patient's side: the dose shows exactly
// as written.
//
// Lives here rather than in the component because the choosing is logic, and
// apps/admin has no test runner; contracts is exercised by the api suite.
export function latestMeetingPrescriptionNote(
  meetings: readonly ClinicianPatientMeeting[],
): { text: string; at: string } | null {
  for (const m of meetings) {
    if (m.workflowStatus !== "documented") continue;
    const text = m.documentation?.prescription?.trim();
    // A later documented meeting that wrote NOTHING is a doctor who chose to
    // write nothing. Falling back to an older note would put words in her
    // mouth, so the search stops at the newest documented meeting either way.
    if (!text) return null;
    return { text, at: m.documentation?.documentedAt ?? m.scheduledAt };
  }
  return null;
}

// TES-956 — where the treatment decision's order fields are prefilled from.
//
// The newest DOCUMENTED meeting decides, exactly as for the note above: a
// later meeting that wrote nothing is a doctor who chose to write nothing,
// and an older meeting's order is not put in her mouth. On that meeting the
// structured order wins; its legacy free-text note is parsed only when the
// meeting has no structured order (documented before TES-956).
//
// TES-1031 — the dose goes through `prefillDose`: a meeting draft may hold a
// dose the approval refuses (the draft is not validated, so "0.25" was saved
// at the meeting and then prefilled into an approval that could not be sent).
// Such a dose is left out and returned as `doseLeftOut`, so the form can say
// why the field is empty instead of silently dropping what was written.
export type MeetingOrderPrefill =
  | {
      source: "structured";
      at: string;
      order: Partial<PrescriptionOrder>;
      doseLeftOut: string | null;
    }
  | {
      source: "legacy_note";
      at: string;
      order: Partial<PrescriptionOrder>;
      doseLeftOut: string | null;
      text: string;
      multipleDoses: boolean;
    };

/** The order with its dose made safe to prefill, and what was left out. */
function withPrefillDose(order: Partial<PrescriptionOrder>): {
  order: Partial<PrescriptionOrder>;
  doseLeftOut: string | null;
} {
  const raw = order.dose?.trim();
  if (!raw) return { order, doseLeftOut: null };
  const rest = { ...order };
  delete rest.dose;
  const dose = prefillDose(raw);
  return dose
    ? { order: { ...rest, dose }, doseLeftOut: null }
    : { order: rest, doseLeftOut: raw };
}

export function meetingOrderPrefill(
  meetings: readonly ClinicianPatientMeeting[],
): MeetingOrderPrefill | null {
  const newest = meetings.find((m) => m.workflowStatus === "documented");
  if (!newest) return null;
  const doc = newest.documentation;
  const at = doc?.documentedAt ?? newest.scheduledAt;
  const structured = doc?.proposedOrder;
  if (structured && !meetingOrderIsEmpty(structured)) {
    const order: Partial<PrescriptionOrder> = {};
    if (structured.medication.trim())
      order.medication = structured.medication.trim();
    if (structured.dose.trim()) order.dose = structured.dose.trim();
    if (structured.frequency.trim())
      order.frequency = structured.frequency.trim();
    if (structured.duration.trim()) order.duration = structured.duration.trim();
    return { source: "structured", at, ...withPrefillDose(order) };
  }
  const note = latestMeetingPrescriptionNote([newest]);
  if (!note) return null;
  const parsed = parsePrescriptionNote(note.text);
  return {
    source: "legacy_note",
    at: note.at,
    ...withPrefillDose(parsed.order),
    text: note.text,
    multipleDoses: parsed.multipleDoses,
  };
}

// TES-519 — the renewal decision moved from the work-queue row onto the
// patient screen: the cycle under review and what the patient answered to
// ask for it. Null when no cycle is waiting on the doctor.
// TES-1443 — a register symptom she chose in the renewal, as it was written
// to her SymptomEntry (sourceSurface "renewal"). Values are the register's
// (SYMPTOM_QUESTIONS); null where she left an optional question unanswered.
export const renewalSymptomAnswerSchema = z.object({
  symptomCode: z.string(),
  severity: z.string(),
  trend: z.string().nullable(),
  intake: z.string().nullable(),
  startedOn: z.string().nullable(),
  note: z.string().nullable(),
  /** none | severe | acute: the safety outcome she was shown. */
  safetyLevel: z.string(),
});
export type RenewalSymptomAnswer = z.infer<typeof renewalSymptomAnswerSchema>;

export const renewalAnswersSchema = z.object({
  currentWeightKg: z.number(),
  doingWell: z.string(),
  sideEffects: z.string().nullable(),
  questionsForDoctor: z.string().nullable(),
  submittedAt: z.string(),
  /** TES-1443 — in the register's order; empty when she chose none. */
  symptoms: z.array(renewalSymptomAnswerSchema),
});
export type RenewalAnswers = z.infer<typeof renewalAnswersSchema>;

// TES-1637 (Emelie 2026-10-28) — what she wants to discuss with the doctor,
// from her latest F-05, shown with the monthly evidence: "Vad vill du
// diskutera med läkaren?", the text under "Annat" (F-05 v15 on) and, on an
// older version, "Vilken fråga vill du helst ha svar på?". Labels and
// answers in the wording of the version she answered.
// TES-1638 (2026-10-29) — from F-05 v16 the items are the dose wish ("Vad
// önskar du inför läkarens bedömning av din dos?"), "Vill du ta upp något
// annat om din medicinska behandling?" and the text under "Annan medicinsk
// fråga". `value` is the stored option value of a choice, so the admin can
// point out a wish to pause or stop; optional so an older API response
// still parses.
export const monthlyDiscussionSchema = z.object({
  submissionId: z.string(),
  submittedAt: z.string(),
  templateVersion: z.number().int(),
  items: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      answer: z.string(),
      value: z.string().optional(),
    }),
  ),
});
export type MonthlyDiscussion = z.infer<typeof monthlyDiscussionSchema>;

// TES-1637 — the doctor's follow-up question in the monthly case:
// "waiting" ("Inväntar patientsvar") until she answers, then "answered"
// (back in his queue) until he handles the answer.
export const monthlyFollowUpSchema = z.object({
  status: z.enum(["waiting", "answered"]),
  since: z.string(),
});
export type MonthlyFollowUp = z.infer<typeof monthlyFollowUpSchema>;

export const clinicianRenewalReviewSchema = z.object({
  cycleId: z.string().uuid(),
  cycleNumber: z.number().int(),
  state: z.string(),
  dose: z.string().nullable(),
  // TES-636 — the whole current prescription, so the renewal panel can
  // PRE-FILL the fields instead of requiring the doctor to rewrite them every
  // cycle. The prescription is mandatory now; without pre-filling there would
  // be three fields to copy by hand at every unchanged renewal, and
  // transcription is exactly where errors arise on this surface.
  medication: z.string().nullable(),
  frequency: z.string().nullable(),
  // TES-956 — the treatment length, prefilled like the rest. Optional so an
  // older API response still parses.
  duration: z.string().nullable().optional(),
  expiresAt: z.string().nullable(),
  answers: renewalAnswersSchema.nullable(),
  // TES-1431 (TES-1425 §6) — her due F-05, while it is unsubmitted: the
  // renewal cannot continue her treatment until she has sent it. Null when
  // nothing blocks. Optional so an older API response still parses.
  monthlyCheckinMissing: z
    .object({ monthIndex: z.number().int(), dueAt: z.string() })
    .nullable()
    .optional(),
  // TES-1637 — her discussion answers from her latest F-05, and the case's
  // follow-up question. Null when there is none; optional so an older API
  // response still parses.
  monthlyDiscussion: monthlyDiscussionSchema.nullable().optional(),
  followUp: monthlyFollowUpSchema.nullable().optional(),
});
export type ClinicianRenewalReview = z.infer<
  typeof clinicianRenewalReviewSchema
>;

// TES-519 — an open (uncleared) MedicalRiskFlag, pinned at the top of the
// patient's overview with its own CTA. The work-queue lane counts them; this
// is what the doctor acts on.
export const clinicianRiskFlagSchema = z.object({
  id: z.string(),
  kind: z.string(),
  rule: z.string().nullable(),
  raisedAt: z.string(),
  // TES-676 — receipt is a separate fact from clearance. Optional so an
  // older API response still parses.
  acknowledgedAt: z.string().nullable().optional(),
  // TES-698 — when the fallback route fired (SLA passed without receipt).
  escalatedAt: z.string().nullable().optional(),
  // TES-948 — what raised it ("FormSubmission" + its id for a form's hard
  // stop), so the alarm sits on the same row as the flag in "Riskflaggor: X".
  sourceType: z.string().nullable().optional(),
  sourceId: z.string().nullable().optional(),
});
export type ClinicianRiskFlag = z.infer<typeof clinicianRiskFlagSchema>;

export const patientDetailSchema = z.object({
  patient: z.object({
    id: z.string().uuid(),
    name: z.string().nullable(),
    personalNumber: z.string(),
    preferredLocale: z.string(),
    // TES-279 — the canonical journey state (free string on the wire; the
    // vocabulary lives in patient-journey-state.ts).
    journeyState: z.string(),
    // TES-298 — test patient: outbound suppressed, simulate-event allowed,
    // admin shows the Test tab + badge.
    isTest: z.boolean(),
    createdAt: z.string(),
    webdocPatientRef: z.string().nullable(),
    // TES-504 — where her e-mail stands (address itself stays out of the
    // clinician DTO; the doctor needs the state, not the string).
    emailStatus: patientEmailStatusSchema,
    // TES-1106 — may Ella contact her with product-feedback questions: her
    // current choice with its date and wording version (null = never chosen).
    productFeedbackConsent: productFeedbackConsentStateSchema.nullable(),
  }),
  treatment: z
    .object({
      id: z.string().uuid(),
      state: z.string(),
      startedAt: z.string(),
      endedAt: z.string().nullable(),
      cycles: z.array(patientDetailCycleSchema),
    })
    .nullable(),
  // CAR-117 — the patient's lab orders (most recent first) with resulted
  // analytes, so the doctor can review + approve before activating treatment.
  labOrders: z.array(clinicianLabOrderSchema),
  // TES-687 — whether the labs gate is WAIVED for this patient. The gate has
  // two doors (a resulted+reviewed LabOrder, or a documented meeting whose
  // next step was `labs_verified_trusted_source`), but only the first was
  // visible to admin, so "Godkänn" stayed disabled in exactly the launch
  // path — where results are verified outside Ella and no LabOrder ever
  // exists. Sent rather than recomputed: the rule is the server's, and a
  // second copy drifts.
  labsWaived: z.boolean(),
  // Which of the two journeys the patient is on, derived from her own F-01
  // answers on every read (never stored — see patient-group.ts). The doctor
  // has to know: a PRESCRIPTION_TRANSFER patient must not be sent to a
  // consultation, and nothing else on this screen says so.
  patientGroup: patientGroupSchema,
  // When F-01 was answered, and from which of its two homes: a `f01`
  // FormSubmission, or the Lead row the web funnel wrote before signup.
  // Null when she has answered nowhere — which is why the group above can
  // read NEW_TREATMENT without her having chosen it.
  f01: z
    .object({
      submittedAt: z.string(),
      source: z.enum(["form_submission", "lead"]),
      // TES-524 — the instrument version the answers were given against
      // (null for a lead written before the web quiz recorded one), and
      // Q12 / SUIT_DESIRED_OUTCOME as today's option values: the ONE goal
      // data point F-02 reuses, shown to the doctor with its provenance.
      templateVersion: z.number().int().nullable(),
      desiredOutcome: z.array(z.string()),
      desiredOutcomeOther: z.string().nullable(),
      // TES-524 §2 — SUIT_REPRODUCTIVE_STATUS as answered in F-01 (today's
      // option value), the other data point F-02 reuses; null on a v4 F-01.
      reproductiveStatus: z.string().nullable().optional(),
      // TES-1322 — SUIT_DESIRED_SUPPORT (F-01 v11) as the codes she chose,
      // and the "Annat" text when OTHER is among them. Her stated wish, shown
      // to the doctor apart from flags and decision support; never a finding.
      // Empty / null for v10 and earlier, which never asked. Optional so an
      // older API response parses.
      desiredSupport: z.array(z.string()).optional(),
      desiredSupportOther: z.string().nullable().optional(),
    })
    .nullable(),
  // TES-1164 (Ö-383) — type 1 diabetes and MTC/MEN 2, one row per question
  // asked anywhere: her F-01 answer, or else her F-02 answer
  // (resolveF01SafetyAnswers). Optional so an older API response parses.
  f01SafetyAnswers: z.array(f01SafetyAnswerSchema).optional(),
  formSubmissions: z.array(formSubmissionSchema),
  // CAR-118: deterministic pre-consult summary built from the latest intake
  // submission + the version it was submitted against. Null when the patient
  // has no intake yet (or its template version is no longer resolvable).
  preConsultSummary: preConsultSummarySchema.nullable(),
  // TES-1071 (TES-1069 point 14) — where she stands in the ONE
  // treatment-start state machine, derived on the server from stored facts.
  // Since TES-1069 a positive new-treatment decision leaves the treatment in
  // `onboarding` until "Bekräfta recept och publicera behandlingen", so the
  // treatment state alone no longer says whether a decision is published.
  // Optional so an older API response parses.
  treatmentStartState: treatmentStartStateSchema.optional(),
  // TES-457 — the open-or-latest transfer review. Only ever set for a
  // PRESCRIPTION_TRANSFER patient; null before F-02 + F-07 are both in.
  transferReview: transferReviewSchema.nullable(),
  // TES-506 — the patient's care meetings, with what was documented at them.
  //
  // The 2026-09-04 walkthrough asked for the care meeting as a tab under the
  // patient, and noted that the notes could not be found afterwards (nor the
  // prescriptions). The note sat on the meeting all along — there was just no
  // way back to it without knowing the meeting's id. Newest first.
  meetings: z.array(clinicianPatientMeetingSchema),
  // TES-519 — see the two schemas above. Optional so an older API parses.
  riskFlags: z.array(clinicianRiskFlagSchema).optional(),
  renewalReview: clinicianRenewalReviewSchema.nullable().optional(),
  // TES-506 — the four numbers the doctor asked to see before anything else:
  // current weight, goal weight, status and flags — put simply, the same as
  // under "Dina värden". This is literally the patient's own
  // "Dina värden" snapshot (TES-218 goalProgress), not a second derivation of
  // it — a doctor and a patient disagreeing about her weight because two code
  // paths rounded differently is not a bug anyone would find quickly.
  //
  // Optional so a bundle newer than its API still renders the page (the card
  // simply does not appear).
  goalProgress: goalProgressSchema.optional(),
  // TES-998 (Ö-238) — the goal zone as the doctor needs it: the latest
  // confirmation (a keep-wish waiting for his next monthly assessment shows
  // here) and her F-02 "Har du redan nått din målvikt eller målzon?" answer.
  // Optional for the usual mixed-version reason.
  goalZone: goalZoneDoctorSummarySchema.optional(),
  // TES-736 — what is still open in CP-06, so the approve dialog can warn
  // before the click. Optional: an admin built before this reads a newer
  // server; a server built before this leaves it out.
  followUpOutstanding: z.array(followUpItemSchema).optional(),
  // TES-944 (TES-941 §PATIENTHEADER) — the header's separate fields, derived
  // on the server from the same reads the rest of this DTO makes (see
  // patient-header.ts). All optional so an admin built before this reads a
  // newer server, and a newer admin renders against an older one.
  //
  // "Klinisk fas", one of Emelie's seven values.
  clinicalStage: clinicalStageSchema.optional(),
  // "Abonnemang": Stripe status + plan, and the standing derived from them.
  subscription: clinicianSubscriptionSchema.optional(),
  // The newest standing blood pressure / pulse reading; null when none.
  bloodPressurePulse: bloodPressurePulseReadingSchema.nullable().optional(),
  // "Riskflaggor: X" — every flag and hard stop across ALL her submissions
  // that does NOT carry an open acute alarm, counted with the same
  // splitRiskFlagRows the overview's list renders, so the header's number and
  // the list's length cannot differ. TES-1001 (Ö-250): an alarm is counted in
  // `acuteAlarmCount` only, never here as well.
  formFlagCount: z.number().int().nonnegative().optional(),
  // TES-1001 (Ö-250) — "Akutlarm: X – agera nu": the open acute alarms, each
  // counted once whether or not a form's hard stop carries it.
  acuteAlarmCount: z.number().int().nonnegative().optional(),
  // TES-1117 — the overview's onboarding card: journey, precise status, next
  // step, who we wait on and the latest activity. The same derivation as the
  // patient list row. Optional for the usual mixed-version reason.
  onboardingStatus: onboardingStatusSchema.optional(),
  // TES-1117 S2 — the timeline the onboarding card opens, built from the
  // same facts as `onboardingStatus`. Optional for the same reason.
  onboardingTimeline: z.array(onboardingTimelineStepSchema).optional(),
  // TES-1554 — the reasons the doctor picks between when she ends the drug
  // treatment. Empty while the tenant flag `unsuitableEndRefundTask` is off:
  // the end then takes no reason, as before. Optional for the usual
  // mixed-version reason.
  treatmentEndReasons: z.array(treatmentEndReasonSchema).optional(),
});
export type PatientDetail = z.infer<typeof patientDetailSchema>;
